import { useEffect, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';
import { TranslationService } from '../../../../../services/translation.service';
import { ButtonDivIcon } from '../button-div-icon';

export interface IGenericLocation {
  text: string;
  altText: string;
  latitude: number;
  longitude: number;
  autoOpenPopup: boolean;
}

function GenericLocationMarker({ marker }: { marker: IGenericLocation }) {
  const markerRef = useRef<L.Marker>(null!);

  useEffect(() => {
    if (marker.autoOpenPopup) {
      setTimeout(() => {
        markerRef.current?.openPopup();
      }, 100);
    }
  }, [marker, markerRef]);

  const icon = new ButtonDivIcon({
    className: 'button-div-icon generic-location-marker',
    iconSize: [42, 55],
    iconAnchor: [21, 55],
    popupAnchor: [0, -50],
    html: `<img src="/icons/map-marker-house-blue-1000.svg" alt="${marker.altText}" class="w-100 h-100" />`, // The svg is placed in the wwwroot/icons folder (i.e. not copied by vite from the assets folder at build time)
  });

  function handleOnPopupOpen(popupEvent: L.PopupEvent) {
    // Inspired by http://melmo.github.io/accessibility/code/dist/leaflet.html
    // Focus on the opened popup
    const popup = popupEvent.popup.getElement();
    if (popup) {
      popup.setAttribute('tabindex', '-1');
      popup.style.outline = 'none'; // Workaround for hiding built-in focus marker, see NV-4097
      popup.focus();
    }

    // Fix the a11y attributes on the close button
    const closeButtons = popupEvent.popup.getElement()?.getElementsByClassName('leaflet-popup-close-button');
    if (closeButtons && closeButtons.length > 0) {
      const closeButton = closeButtons[0] as HTMLLinkElement;
      closeButton.setAttribute('role', 'button');
      closeButton.setAttribute('aria-label', TranslationService.get('common.map.closepopup'));
      closeButton.href = '#';
    }
  }

  return (
    <Marker
      ref={markerRef}
      key={`${marker.latitude}-${marker.longitude}`}
      icon={icon}
      position={[marker.latitude, marker.longitude]}
      eventHandlers={{
        popupopen: handleOnPopupOpen,
      }}
    >
      <Popup autoPan={true} offset={[0, 0]} content={marker.text}></Popup>
    </Marker>
  );
}

export default GenericLocationMarker;
